import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Check } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useCMSContent, getSectionByType } from '../hooks/useCMSContent';
import axios from 'axios';
import { brandTitle } from '../seo/seoCore';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const ContactPage = () => {
  const { sections, seo } = useCMSContent('contact');
  const heroSection = getSectionByType(sections, 'hero');
  const infoSection = sections?.find(s => s.id === 'contact-info' || s.type === 'text');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
    project_type: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (seo?.title) document.title = brandTitle(seo.title);
  }, [seo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {/* Hero - CMS Driven */}
      <section className="pt-32 pb-20 bg-neutral-50">
        <div className="container-xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <span className="label-overline text-amber-700">{heroSection?.subtitle || 'Get In Touch'}</span>
            <h1 className="mt-4 text-neutral-900">{heroSection?.title || 'Contact Apollo Shades'}</h1>
            <p className="mt-6 text-xl text-neutral-600 leading-relaxed">
              {heroSection?.content || "Have questions about our products or services? We're here to help. Reach out and our team will respond within 24 hours."}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="section-padding">
        <div className="container-xl">
          <div className="grid lg:grid-cols-3 gap-12 lg:gap-20">
            {/* Contact Info */}
            <div>
              <h2 className="text-xl mb-8">Contact Information</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <MapPin className="w-5 h-5 text-amber-700 mt-1" />
                  <div>
                    <p className="font-medium">Visit Our Showroom</p>
                    <p className="text-neutral-600 mt-1">
                      Apollo Shades<br />
                      1 Brooklyn Navy Yard, Building 33, Suite 201B<br />
                      Brooklyn, NY 11205
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <Phone className="w-5 h-5 text-amber-700 mt-1" />
                  <div>
                    <p className="font-medium">Phone</p>
                    <a href="tel:2122090654" className="text-neutral-600 mt-1 hover:text-amber-700">(212) 209-0654</a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <Mail className="w-5 h-5 text-amber-700 mt-1" />
                  <div>
                    <p className="font-medium">Email</p>
                    <a href="mailto:info@apolloshades.com" className="text-neutral-600 mt-1 hover:text-amber-700">info@apolloshades.com</a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <Clock className="w-5 h-5 text-amber-700 mt-1" />
                  <div>
                    <p className="font-medium">Business Hours</p>
                    <p className="text-neutral-600 mt-1">
                      Showroom visits by appointment
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              {submitted ? (
                <div className="bg-neutral-50 p-12 text-center">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check className="w-8 h-8 text-amber-600" />
                  </div>
                  <h3 className="text-xl mb-2">Message Received</h3>
                  <p className="text-neutral-600">Thank you for contacting us. Our team will respond within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="label-overline">Full Name *</Label>
                      <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="mt-2" />
                    </div>
                    <div>
                      <Label className="label-overline">Email *</Label>
                      <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="mt-2" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="label-overline">Phone</Label>
                      <Input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="mt-2" />
                    </div>
                    <div>
                      <Label className="label-overline">Company</Label>
                      <Input value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} className="mt-2" />
                    </div>
                  </div>
                  <div>
                    <Label className="label-overline">Project Type</Label>
                    <select
                      value={formData.project_type}
                      onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
                      className="w-full mt-2 input-form"
                    >
                      <option value="">Select...</option>
                      <option value="residential">Residential</option>
                      <option value="commercial">Commercial</option>
                      <option value="trade">Trade Inquiry</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <Label className="label-overline">Subject *</Label>
                    <Input value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} required className="mt-2" />
                  </div>
                  <div>
                    <Label className="label-overline">Message *</Label>
                    <Textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} required rows={6} className="mt-2" />
                  </div>
                  <Button type="submit" disabled={loading} className="btn-primary">
                    {loading ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="h-96 bg-neutral-200">
        <iframe
          src="https://www.google.com/maps?q=1+Brooklyn+Navy+Yard,+Building+33,+Brooklyn,+NY+11205&output=embed"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Apollo Shades Location"
        />
      </section>
    </Layout>
  );
};
